import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import localeEnCA from '@angular/common/locales/en-CA';
import localeFrCA from '@angular/common/locales/fr-CA';
import {
  type ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, TitleStrategy } from '@angular/router';
import { authConfig } from '@core/config/auth.config';
import { httpAuthInterceptor } from '@core/interceptors/auth.interceptor';
import { httpErrorInterceptor } from '@core/interceptors/error.interceptor';
import { httpLoaderInterceptor } from '@core/interceptors/loader.interceptor';
import { httpApiUrlInterceptor } from '@core/interceptors/url.interceptor';
import { LOCALE_PROVIDER } from '@core/providers/locale.provider';
import { BracedMissingTranslationHandler } from '@core/providers/ngx-translate/missing-translation.handler';
import { NamespaceTranslateLoader } from '@core/providers/ngx-translate/translate.loader';
import { TranslatedTitleStrategy } from '@core/providers/ngx-translate/translated-title.strategy';
import { provideTheme } from '@mmscan/design-system';
import { MMSCAN_API_BASE_URL } from '@mmscan/mmscan-api-angular-client';
import type { Locale } from '@mmscan/shared-types';
import {
  provideMissingTranslationHandler,
  provideTranslateLoader,
  provideTranslateService,
  TranslateService,
} from '@ngx-translate/core';
import { provideAuth } from 'angular-auth-oidc-client';
import { MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { firstValueFrom } from 'rxjs';
import { environment } from '../environments/environment';
import { routes } from './routes/app.routes';

// The app is a single bundle: CLDR data for both supported locales must be
// registered explicitly (per-locale builds used to provide it).
registerLocaleData(localeEnCA);
registerLocaleData(localeFrCA);

const userTheme = (localStorage.getItem('theme') as 'light' | 'dark') || 'light';
const userLocale = getCookie('locale') || 'EN_CA';

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? decodeURIComponent(match[2]) : null;
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideAnimationsAsync(),
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(routes),
    provideTheme(userTheme, userLocale as Locale),
    provideHttpClient(
      withFetch(),
      withInterceptors([httpApiUrlInterceptor, httpLoaderInterceptor, httpErrorInterceptor, httpAuthInterceptor])
    ),
    provideAuth(authConfig),
    provideTranslateService({
      loader: provideTranslateLoader(NamespaceTranslateLoader),
      missingTranslationHandler: provideMissingTranslationHandler(BracedMissingTranslationHandler),
      fallbackLang: 'en-CA',
    }),
    // Blocks first render until translations are loaded, so translate.instant()
    // is safe everywhere from app start.
    provideAppInitializer(() => {
      const translate = inject(TranslateService);
      const initialLang = userLocale === 'FR_CA' ? 'fr-CA' : 'en-CA';
      return firstValueFrom(translate.use(initialLang)).catch(() => undefined);
    }),
    { provide: TitleStrategy, useClass: TranslatedTitleStrategy },
    { provide: MMSCAN_API_BASE_URL, useValue: environment.mmscanApiUrl },
    MessageService,
    LOCALE_PROVIDER,
    DialogService,
  ],
};
